import { Button, Checkbox, Grid, Group, Modal, Select, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { createReference, formatHumanName, normalizeErrorString } from '@medplum/core';
import type { Coding, HumanName, Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import { CodingInput, useMedplum } from '@medplum/react';
import {
  CLOUDPAT_HSAT_CODING,
  CPT_SYSTEM,
  HSAT_CPT_CODE,
  HSAT_PATIENT_APPROVAL_PENDING_TAG,
  isInsuredCopayPatient,
} from '@polarissleep/shared';
import { IconX } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '../../components/layout/PageHeader';
import { usePatient } from '../../hooks/usePatient';
import { botIdentifier } from '../../lib/bot-identifier';
import { ICD10_VALUESET } from '../../utils/icd10';
import { withTenantScope } from '../../utils/tenant';
import classes from './OrderPatientHSATPage.module.css';

export const ICD10_CONDITION_VALUESET = ICD10_VALUESET;

const TEST_TYPE_OPTIONS = [
  { value: 'hsat', label: 'HSAT (WatchPAT)' },
  { value: 'hsat-other', label: 'HSAT (Other)' },
];

function Tag({ label, onRemove }: { label: string; onRemove: () => void }): JSX.Element {
  return (
    <span className={classes.tag}>
      {label}
      <span className={classes.tagRemove} onClick={onRemove} role="button" aria-label={`Remove ${label}`}>
        <IconX size={13} />
      </span>
    </span>
  );
}

function getPatientFullName(patient: Patient | undefined): string {
  if (!patient?.name?.[0]) {
    return '';
  }
  return formatHumanName(patient.name[0] as HumanName);
}

function getPatientAddress(patient: Patient | undefined): {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
} {
  const addr = patient?.address?.[0];
  return {
    line1: addr?.line?.[0] ?? '',
    line2: addr?.line?.[1] ?? '',
    city: addr?.city ?? '',
    state: addr?.state ?? '',
    zip: addr?.postalCode ?? '',
  };
}

function getContactValue(patient: Patient | undefined, system: 'phone' | 'email'): string {
  return patient?.telecom?.find((t) => t.system === system)?.value ?? '';
}

export function OrderPatientHSATPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const patient = usePatient();
  const provider = medplum.getProfile() as Practitioner | undefined;

  const patientName = getPatientFullName(patient);
  const patientAddress = getPatientAddress(patient);
  const patientPhone = getContactValue(patient, 'phone');
  const patientEmail = getContactValue(patient, 'email');
  const patientDOB = patient?.birthDate ?? '';

  const providerFirstName = provider?.name?.[0]?.given?.[0] ?? '';
  const providerLastName = provider?.name?.[0]?.family ?? '';
  const providerEmail = provider?.telecom?.find((t) => t.system === 'email')?.value ?? '';
  const providerPhone = provider?.telecom?.find((t) => t.system === 'phone')?.value ?? '';
  const providerNPI = provider?.identifier?.find((id) => id.system?.includes('npi'))?.value ?? '';

  // Suspected Diagnosis form state
  const [suspectedDiagnosis, setSuspectedDiagnosis] = useState<Coding | undefined>(undefined);
  const [errors, setErrors] = useState<{
    suspectedDiagnosis?: string;
    clinicalRationale?: string;
    supportingIndications?: string;
    otherVendor?: string;
  }>({});
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState<Coding | null>(null);
  const CLINICAL_RATIONALE_OPTIONS = ['STOP-BANG ≥ 3', 'Epworth ≥ 8'];
  const [clinicalRationaleValues, setClinicalRationaleValues] = useState<string[]>([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState('');
  const SUPPORTING_INDICATIONS_OPTIONS = [
    'Snoring (R06.83)',
    'Witnessed apnea (R06.81)',
    'Somnolence (R40.0)',
    'Hypersomnia, unspecified (G47.10)',
    'Other hypersomnia (G47.19)',
    'Sleep disorder, unspecified (G47.9)',
    'Insomnia, unspecified (G47.00)',
    'Other sleep disorders (G47.8)',
    'Shortness of breath (R06.02)',
    'Hypoxemia (R09.02)',
    'Fatigue (R53.83)',
    'Essential hypertension (I10)',
    'Obesity (E66.9)',
    'Morbid obesity (E66.01)',
    'Type 2 diabetes (E11.9)',
    'Other: Specify',
  ];
  const [supportingIndicationsTags, setSupportingIndicationsTags] = useState<string[]>([]);
  const [showOtherIndicationInput, setShowOtherIndicationInput] = useState(false);
  const [otherIndicationValue, setOtherIndicationValue] = useState('');
  const [notes, setNotes] = useState('');
  const [testType, setTestType] = useState('hsat');
  const [otherVendor, setOtherVendor] = useState('');

  // Insurance form state
  const [planName, setPlanName] = useState('');
  const [planCarrier, setPlanCarrier] = useState('');
  const [memberId, setMemberId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [insurancePhone, setInsurancePhone] = useState('');

  const [signatureOpened, { open: openSignature, close: closeSignature }] = useDisclosure(false);
  const [signatureChecked, setSignatureChecked] = useState(false);
  const providerFullName = `${providerFirstName} ${providerLastName}`.trim();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    const newErrors: typeof errors = {};
    if (!suspectedDiagnosis) {
      newErrors.suspectedDiagnosis = 'Suspected Diagnosis is required';
    }
    if (clinicalRationaleValues.length === 0) {
      newErrors.clinicalRationale = 'Clinical Rationale is required';
    }
    if (supportingIndicationsTags.length === 0) {
      newErrors.supportingIndications = 'Supporting Indications is required';
    }
    if (testType === 'hsat-other' && !otherVendor.trim()) {
      newErrors.otherVendor = 'Company name is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    if (!patient?.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      const isOtherHsat = testType === 'hsat-other';
      const serviceRequest: ServiceRequest = {
        resourceType: 'ServiceRequest',
        meta: { tag: [HSAT_PATIENT_APPROVAL_PENDING_TAG] },
        status: 'active',
        intent: 'order',
        subject: createReference(patient),
        requester: provider ? createReference(provider) : undefined,
        code: {
          coding: [
            ...(isOtherHsat ? [] : [CLOUDPAT_HSAT_CODING]),
            { system: CPT_SYSTEM, code: HSAT_CPT_CODE, display: 'Home sleep apnea test (HSAT)' },
            ...(!isOtherHsat
              ? [{ system: 'http://snomed.info/sct', code: '276734008', display: 'Home sleep apnea test (WatchPAT)' }]
              : []),
          ],
          text: `Home Sleep Apnea Test (HSAT) - ${isOtherHsat ? otherVendor.trim() : 'WatchPAT'}`,
        },
        reasonCode: [
          ...(suspectedDiagnosis ? [{ coding: [suspectedDiagnosis], text: suspectedDiagnosis.display }] : []),
          ...(secondaryDiagnosis ? [{ coding: [secondaryDiagnosis], text: secondaryDiagnosis.display }] : []),
        ],
        note: [
          ...(clinicalRationaleValues.length > 0
            ? [{ text: `Clinical Rationale: ${clinicalRationaleValues.join(', ')}` }]
            : []),
          ...(supportingIndicationsTags.length > 0
            ? [{ text: `Supporting Indications: ${supportingIndicationsTags.join(', ')}` }]
            : []),
          ...(notes ? [{ text: `Notes: ${notes}` }] : []),
          { text: `Signature: ${providerFullName}` },
          ...(isOtherHsat ? [{ text: `HSAT Vendor: ${otherVendor.trim()}` }] : []),
          { text: `Plan Name: ${planName}` },
          { text: `Plan Carrier: ${planCarrier}` },
          { text: `Member ID: ${memberId}` },
          { text: `Group ID: ${groupId}` },
          { text: `Insurance Phone: ${insurancePhone}` },
        ].filter((n) => n.text && !n.text.endsWith(': ')),
        occurrenceDateTime: new Date().toISOString(),
      };

      const createdServiceRequest = await medplum.createResource(withTenantScope(serviceRequest));

      const activeCoverage = await medplum.searchOne('Coverage', {
        beneficiary: `Patient/${patient.id}`,
        status: 'active',
      });
      const insuredPatient = isInsuredCopayPatient(patient) || Boolean(activeCoverage);

      if (!isOtherHsat && insuredPatient && createdServiceRequest.id) {
        await medplum.executeBot(botIdentifier('order-hsat'), {
          patientId: patient.id,
          serviceRequestId: createdServiceRequest.id,
        });
      }

      showNotification({ color: 'green', title: 'Order Placed', message: 'HSAT order submitted successfully.' });
      navigate('/hsat-devices');
    } catch (err) {
      showNotification({ color: 'red', title: 'Error', message: normalizeErrorString(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap={16} className={classes.page} maw={1190} mx="auto">
      {/* Header */}
      <div className={classes.card}>
        <Stack gap={8}>
          <Text className={classes.breadcrumb} onClick={() => navigate(-1)}>
            Session Note {patientName} &gt;
          </Text>
          <PageHeader
            title="Order New Test"
            actions={
              <Button
                variant="outline"
                className={classes.cancelButton}
                onClick={() => navigate(-1)}
                style={{ borderColor: '#640040', color: '#640040' }}
              >
                Cancel
              </Button>
            }
          />
        </Stack>
      </div>

      {/* Suspected Diagnosis */}
      <div className={classes.card}>
        <Stack gap={16}>
          <Title order={2} className={classes.sectionTitle}>
            Suspected Diagnosis
          </Title>
          <Grid gutter={16}>
            <Grid.Col span={6}>
              <CodingInput
                name="suspectedDiagnosis"
                path="Condition.code"
                label="Suspected Diagnosis *"
                binding={ICD10_CONDITION_VALUESET}
                defaultValue={suspectedDiagnosis}
                onChange={(val) => {
                  setSuspectedDiagnosis(val);
                  setErrors((e) => ({ ...e, suspectedDiagnosis: undefined }));
                }}
                outcome={
                  errors.suspectedDiagnosis
                    ? {
                        resourceType: 'OperationOutcome',
                        issue: [{ severity: 'error', code: 'required', details: { text: errors.suspectedDiagnosis } }],
                      }
                    : undefined
                }
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <CodingInput
                name="secondaryDiagnosis"
                path="Condition.code"
                label="Secondary Suspected Diagnosis"
                binding={ICD10_CONDITION_VALUESET}
                defaultValue={secondaryDiagnosis ?? undefined}
                onChange={(coding) => setSecondaryDiagnosis(coding ?? null)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={4}>
                <Select
                  label="Clinical Rationale *"
                  placeholder="Select or type to add..."
                  data={CLINICAL_RATIONALE_OPTIONS.filter(
                    (o) => o === 'Other: Specify' || !clinicalRationaleValues.includes(o)
                  )}
                  value={null}
                  onChange={(val) => {
                    if (!val) {
                      return;
                    }
                    if (val === 'Other: Specify') {
                      setShowOtherInput(true);
                    } else if (!clinicalRationaleValues.includes(val)) {
                      setClinicalRationaleValues((prev) => [...prev, val]);
                      setErrors((e) => ({ ...e, clinicalRationale: undefined }));
                    }
                  }}
                  searchable
                  clearable={false}
                />
                {showOtherInput && (
                  <TextInput
                    placeholder="Type and press Enter to add..."
                    value={otherInputValue}
                    onChange={(e) => setOtherInputValue(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const trimmed = otherInputValue.trim();
                        if (trimmed && !clinicalRationaleValues.includes(trimmed)) {
                          setClinicalRationaleValues((prev) => [...prev, trimmed]);
                        }
                        setOtherInputValue('');
                        setShowOtherInput(false);
                      }
                    }}
                    autoFocus
                  />
                )}
                {clinicalRationaleValues.length > 0 && (
                  <Group gap={4} wrap="wrap">
                    {clinicalRationaleValues.map((val) => (
                      <Tag
                        key={val}
                        label={val}
                        onRemove={() => setClinicalRationaleValues((prev) => prev.filter((v) => v !== val))}
                      />
                    ))}
                  </Group>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={4}>
                <Select
                  label="Supporting Indications *"
                  placeholder="Select or type to add..."
                  data={SUPPORTING_INDICATIONS_OPTIONS.filter(
                    (o) => o === 'Other: Specify' || !supportingIndicationsTags.includes(o)
                  )}
                  value={null}
                  onChange={(val) => {
                    if (!val) {
                      return;
                    }
                    if (val === 'Other: Specify') {
                      setShowOtherIndicationInput(true);
                    } else if (!supportingIndicationsTags.includes(val)) {
                      setSupportingIndicationsTags((prev) => [...prev, val]);
                      setErrors((e) => ({ ...e, supportingIndications: undefined }));
                    }
                  }}
                  searchable
                  clearable={false}
                />
                {showOtherIndicationInput && (
                  <TextInput
                    placeholder="Type and press Enter to add..."
                    value={otherIndicationValue}
                    onChange={(e) => setOtherIndicationValue(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const trimmed = otherIndicationValue.trim();
                        if (trimmed && !supportingIndicationsTags.includes(trimmed)) {
                          setSupportingIndicationsTags((prev) => [...prev, trimmed]);
                        }
                        setOtherIndicationValue('');
                        setShowOtherIndicationInput(false);
                      }
                    }}
                    autoFocus
                  />
                )}
                {supportingIndicationsTags.length > 0 && (
                  <Group gap={4} wrap="wrap">
                    {supportingIndicationsTags.map((tag) => (
                      <Tag
                        key={tag}
                        label={tag}
                        onRemove={() => setSupportingIndicationsTags((prev) => prev.filter((t) => t !== tag))}
                      />
                    ))}
                  </Group>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                label="Notes"
                placeholder="Patient has concerningly elevated pAHIc (3%), want to rule out OSA."
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                minRows={3}
                autosize
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Test Type"
                data={TEST_TYPE_OPTIONS}
                value={testType}
                onChange={(value) => {
                  setTestType(value ?? 'hsat');
                  setOtherVendor('');
                  setErrors((error) => ({ ...error, otherVendor: undefined }));
                }}
              />
              {testType === 'hsat-other' && (
                <TextInput
                  label="Company Name *"
                  placeholder="Enter HSAT company name"
                  value={otherVendor}
                  onChange={(event) => {
                    setOtherVendor(event.currentTarget.value);
                    setErrors((error) => ({ ...error, otherVendor: undefined }));
                  }}
                  error={errors.otherVendor}
                />
              )}
            </Grid.Col>
          </Grid>
        </Stack>
      </div>

      {/* Provider Information */}
      <div className={classes.card}>
        <Stack gap={16}>
          <Group justify="space-between" align="center">
            <Title order={2} className={classes.sectionTitle}>
              Provider Information
            </Title>
            <Button className={classes.editButton} onClick={() => navigate(`/Practitioner/${provider?.id}`)}>
              Edit in Account Settings
            </Button>
          </Group>
          <Grid gutter={16}>
            <Grid.Col span={6}>
              <TextInput label="Title" placeholder="Dr." disabled value="" />
            </Grid.Col>
            <Grid.Col span={6}>
              <Group gap={8} align="flex-end" grow>
                <TextInput label="First Name" value={providerFirstName} disabled />
                <TextInput label="Last Name" value={providerLastName} disabled />
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Email" placeholder="josh.brown@healthworx.studio" value={providerEmail} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Phone" placeholder="+1 (503) 382-3038" value={providerPhone} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="NPI" placeholder="102904992" value={providerNPI} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Degree Type(s)" placeholder="Doctor of Medicine (MD)" disabled value="" />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Certification(s)"
                placeholder="American Board of Sleep Medicine (ABSM)"
                disabled
                value=""
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="DEA Number" placeholder="AB1234567" disabled value="" />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="PTAN" placeholder="004921" disabled value="" />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Taxonomy" placeholder="207RS0012X" disabled value="" />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Date of Birth" placeholder="10/21/2000" disabled value="" />
            </Grid.Col>
          </Grid>
        </Stack>
      </div>

      {/* Patient Information */}
      <div className={classes.card}>
        <Stack gap={16}>
          <Group justify="space-between" align="center">
            <Title order={2} className={classes.sectionTitle}>
              Patient Information
            </Title>
            <Button className={classes.editButton} onClick={() => navigate(`/Patient/${patient?.id}`)}>
              Edit in Patient Chart
            </Button>
          </Group>
          <Grid gutter={16}>
            <Grid.Col span={6}>
              <TextInput label="Title" placeholder="Mr." disabled value="" />
            </Grid.Col>
            <Grid.Col span={6}>
              <Group gap={8} align="flex-end" grow>
                <TextInput
                  label="First Name"
                  value={patient?.name?.[0]?.given?.[0] ?? ''}
                  disabled
                  styles={{ input: { color: '#868e96' } }}
                />
                <TextInput
                  label="Last Name"
                  value={patient?.name?.[0]?.family ?? ''}
                  disabled
                  styles={{ input: { color: '#868e96' } }}
                />
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Email" value={patientEmail} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Phone Number" value={patientPhone} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Date of Birth" value={patientDOB} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Street Address" value={patientAddress.line1} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Unit 2" value={patientAddress.line2} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="City" value={patientAddress.city} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="State" value={patientAddress.state} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Zipcode" value={patientAddress.zip} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <Group gap={8} align="flex-end" grow>
                <TextInput label="Height" value="" disabled styles={{ input: { color: '#868e96' } }} />
                <TextInput label="Weight" value="" disabled styles={{ input: { color: '#868e96' } }} />
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Sex at Birth" value="" disabled />
            </Grid.Col>
          </Grid>
        </Stack>
      </div>

      {/* Insurance */}
      <div className={classes.card}>
        <Stack gap={16}>
          <Group justify="space-between" align="center">
            <Title order={2} className={classes.sectionTitle}>
              Insurance
            </Title>
            <Button className={classes.editButton} onClick={() => navigate(`/Patient/${patient?.id}`)}>
              Edit in Patient Chart
            </Button>
          </Group>
          <Grid gutter={16}>
            <Grid.Col span={6}>
              <TextInput
                label="Plan Name"
                placeholder="BluePreferred PPO Essential Gold 500"
                value={planName}
                onChange={(e) => setPlanName(e.currentTarget.value)}
                disabled
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Plan Carrier"
                placeholder="CareFirst BlueCross BlueShield"
                value={planCarrier}
                onChange={(e) => setPlanCarrier(e.currentTarget.value)}
                disabled
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Member ID"
                placeholder="10299859120"
                value={memberId}
                onChange={(e) => setMemberId(e.currentTarget.value)}
                disabled
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Group ID"
                placeholder="10299859120"
                value={groupId}
                onChange={(e) => setGroupId(e.currentTarget.value)}
                disabled
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Phone Number"
                placeholder="(603) 937-0481 ext. 120"
                value={insurancePhone}
                onChange={(e) => setInsurancePhone(e.currentTarget.value)}
                disabled
              />
            </Grid.Col>
          </Grid>
        </Stack>
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        <Group align="center" gap={16}>
          <div className={classes.signatureArea} onClick={openSignature} style={{ cursor: 'pointer' }}>
            {signatureChecked ? providerFullName : 'Signature'}
          </div>
          <Modal
            opened={signatureOpened}
            onClose={closeSignature}
            centered
            withCloseButton={false}
            styles={{
              content: { borderRadius: 8, padding: 0 },
              body: { padding: 0 },
            }}
          >
            <Stack gap={0}>
              <Group px="lg" py="md" align="center" style={{ borderBottom: '1px solid #dee2e6' }}>
                <Text fw={700} size="lg" c="#640040">
                  Signature
                </Text>
              </Group>
              <Stack gap="md" p="lg">
                <Checkbox
                  label={
                    <Text size="sm">
                      I,{' '}
                      <Text span fw={700}>
                        {providerFullName || 'Provider'}
                      </Text>
                      , confirm that the information provided is accurate and I authorize this order.
                    </Text>
                  }
                  checked={signatureChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignatureChecked(e.currentTarget.checked)}
                  color="#640040"
                />
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="outline"
                    style={{ borderColor: '#640040', color: '#640040' }}
                    onClick={() => {
                      setSignatureChecked(false);
                      closeSignature();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    style={{ backgroundColor: '#640040', color: 'white' }}
                    onClick={closeSignature}
                    disabled={!signatureChecked}
                  >
                    Confirm
                  </Button>
                </Group>
              </Stack>
            </Stack>
          </Modal>
          <Button
            variant="outline"
            style={{
              borderColor: '#640040',
              color: '#640040',
              height: 50,
              paddingInline: 24,
            }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            style={{
              backgroundColor: '#640040',
              height: 50,
              paddingInline: 24,
            }}
            onClick={() => {
              handleSubmit();
            }}
            loading={isSubmitting}
            disabled={
              isSubmitting ||
              !signatureChecked ||
              !suspectedDiagnosis ||
              clinicalRationaleValues.length === 0 ||
              supportingIndicationsTags.length === 0 ||
              (testType === 'hsat-other' && !otherVendor.trim())
            }
          >
            Place Order
          </Button>
        </Group>
      </div>
    </Stack>
  );
}
